import { Box, Flex, Heading, HStack, Tabs, Text, VStack } from '@chakra-ui/react'
import { createFileRoute } from '@tanstack/react-router'
import {
  BoxIcon,
  FileTextIcon,
  KeyIcon,
  NetworkIcon,
  PackageIcon,
  RocketIcon,
  ScrollTextIcon,
  ShieldAlertIcon,
} from 'lucide-react'
import { useState } from 'react'
import {
  ConfigMapsTab,
  DeploymentsTab,
  HelmTab,
  HiveMQLogsTab,
  NamespaceSelector,
  PodsTab,
  SecretsTab,
  ServicesTab,
} from '~/components/k8s'
import { PageHeader } from '~/components/shell'
import { useAuth } from '~/contexts/AuthContext'

export const Route = createFileRoute('/_app/admin/k8s')({
  component: KubernetesPage,
})

function KubernetesPage() {
  const { hasMinRole } = useAuth()
  const [selectedNamespace, setSelectedNamespace] = useState<string>('')
  const [activeTab, setActiveTab] = useState<string>('pods')

  // Check if user has at least operator role
  const isOperatorOrAbove = hasMinRole('operator')
  const isAdmin = hasMinRole('admin')

  // Redirect if not operator or above
  if (!isOperatorOrAbove) {
    return (
      <Flex h="100%" align="center" justify="center">
        <VStack gap={4} textAlign="center" p={8}>
          <Box p={4} borderRadius="full" bg="danger.subtle">
            <ShieldAlertIcon size={48} color="var(--chakra-colors-red-500)" />
          </Box>
          <Heading size="lg" color="danger.fg">
            Access Denied
          </Heading>
          <Text color="content.secondary" maxW="400px">
            This page is restricted to <strong>operator</strong> or <strong>admin</strong> users
            only.
          </Text>
        </VStack>
      </Flex>
    )
  }

  // Reset to first page when namespace changes
  const handleNamespaceChange = (namespace: string) => {
    setSelectedNamespace(namespace)
  }

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader
        title="Kubernetes"
        subtitle="Monitor and manage your Kubernetes resources"
        actions={<NamespaceSelector value={selectedNamespace} onChange={handleNamespaceChange} />}
      />

      <Tabs.Root value={activeTab} onValueChange={(e) => setActiveTab(e.value)}>
        <Tabs.List>
          <Tabs.Trigger value="pods">
            <HStack gap={2}>
              <BoxIcon size={16} />
              <Text>Pods</Text>
            </HStack>
          </Tabs.Trigger>
          <Tabs.Trigger value="configmaps">
            <HStack gap={2}>
              <FileTextIcon size={16} />
              <Text>ConfigMaps</Text>
            </HStack>
          </Tabs.Trigger>
          <Tabs.Trigger value="secrets">
            <HStack gap={2}>
              <KeyIcon size={16} />
              <Text>Secrets</Text>
            </HStack>
          </Tabs.Trigger>
          <Tabs.Trigger value="deployments">
            <HStack gap={2}>
              <RocketIcon size={16} />
              <Text>Deployments</Text>
            </HStack>
          </Tabs.Trigger>
          <Tabs.Trigger value="services">
            <HStack gap={2}>
              <NetworkIcon size={16} />
              <Text>Services</Text>
            </HStack>
          </Tabs.Trigger>
          <Tabs.Trigger value="helm">
            <HStack gap={2}>
              <PackageIcon size={16} />
              <Text>Helm</Text>
            </HStack>
          </Tabs.Trigger>
          <Tabs.Trigger value="hivemq-logs">
            <HStack gap={2}>
              <ScrollTextIcon size={16} />
              <Text>HiveMQ Logs</Text>
            </HStack>
          </Tabs.Trigger>
        </Tabs.List>

        <Box pt={6}>
          <Tabs.Content value="pods">
            <PodsTab namespace={selectedNamespace} isAdmin={isAdmin} />
          </Tabs.Content>
          <Tabs.Content value="configmaps">
            <ConfigMapsTab namespace={selectedNamespace} isAdmin={isAdmin} />
          </Tabs.Content>
          <Tabs.Content value="secrets">
            <SecretsTab namespace={selectedNamespace} isAdmin={isAdmin} />
          </Tabs.Content>
          <Tabs.Content value="deployments">
            <DeploymentsTab namespace={selectedNamespace} isAdmin={isAdmin} />
          </Tabs.Content>
          <Tabs.Content value="services">
            <ServicesTab namespace={selectedNamespace} isAdmin={isAdmin} />
          </Tabs.Content>
          <Tabs.Content value="helm">
            <HelmTab namespace={selectedNamespace} isAdmin={isAdmin} />
          </Tabs.Content>
          <Tabs.Content value="hivemq-logs">
            <HiveMQLogsTab namespace={selectedNamespace} isAdmin={isAdmin} />
          </Tabs.Content>
        </Box>
      </Tabs.Root>
    </Box>
  )
}
